import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { createPortal } from 'react-dom';
import { Container, Form, Input } from 'semantic-ui-react';

import { searchContent } from '@plone/volto/actions/search/search';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import { asyncConnect } from '@plone/volto/helpers/AsyncConnect';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { useClient } from '@plone/volto/hooks/client/useClient';

import searchMessages from '../../../../components/Search/searchMessages';
import {
  buildSearchUrl,
  getSearchLanguageRoot,
} from '../../../../helpers/search';
import { getActiveLanguage } from '../../../../helpers/homepageUrl';

const parseSearchQuery = (search) => {
  const params = new URLSearchParams(search);
  return Object.fromEntries(params.entries());
};

const Search = () => {
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const isClient = useClient();
  const locale = useSelector((state) => state.intl.locale);
  const items = useSelector((state) => state.search.items) || [];
  const language = getActiveLanguage(location.pathname, locale);

  const query = parseSearchQuery(location.search);
  const searchableText = query.SearchableText;
  const subject = query.Subject;
  const hasSearchTerm = Boolean(searchableText || subject);
  const displayTerm = searchableText || subject;

  const [searchText, setSearchText] = useState('');

  const handleSearchSubmit = useCallback(
    (event) => {
      event.preventDefault();
      const q = searchText?.trim();
      if (q) {
        history.push(buildSearchUrl(language, { SearchableText: q }));
      }
    },
    [history, language, searchText],
  );

  const pageTitle = hasSearchTerm
    ? intl.formatMessage(searchMessages.searchResultsTitle)
    : intl.formatMessage(searchMessages.searchPageTitle);

  return (
    <Container id="page-search">
      <Helmet title={pageTitle} />
      <div className="container">
        <article id="content">
          <header>
            <h1 className="documentFirstHeading csj-heading1">
              {hasSearchTerm ? (
                <FormattedMessage
                  {...searchMessages.searchResultsForTerm}
                  values={{ term: displayTerm }}
                />
              ) : (
                <FormattedMessage {...searchMessages.whatDoYouWantToSearch} />
              )}
            </h1>
          </header>
          <section id="content-core">
            {!hasSearchTerm ? (
              <div className="search-prompt">
                <Form onSubmit={handleSearchSubmit}>
                  <Form.Field className="searchbox">
                    <Input
                      onChange={(_, { value }) => setSearchText(value)}
                      name="SearchableText"
                      value={searchText}
                      action={{ icon: 'search', type: 'submit' }}
                      placeholder={intl.formatMessage(
                        searchMessages.searchPlaceholderUppercase,
                      )}
                      transparent
                      autoFocus
                    />
                  </Form.Field>
                </Form>
              </div>
            ) : (
              <div className="search-results-container">
                {items.map((item) => (
                  <UniversalLink
                    key={item['@id']}
                    item={item}
                    className="search-item-container"
                  >
                    <h4 className="search-item-title csj-heading4">
                      {item.title}
                    </h4>
                    {item.description && (
                      <div className="search-item-description">
                        <span>{item.description}</span>
                      </div>
                    )}
                    <p className="search-item-readmore">
                      <FormattedMessage {...searchMessages.readMoreEllipsis} />
                    </p>
                  </UniversalLink>
                ))}
              </div>
            )}
          </section>
        </article>
      </div>
      {isClient &&
        createPortal(
          <Toolbar
            pathname={location.pathname}
            hideDefaultViewButtons
            inner={<span />}
          />,
          document.getElementById('toolbar'),
        )}
    </Container>
  );
};

export const __test__ = Search;

export default asyncConnect([
  {
    key: 'search',
    promise: ({ location, store: { dispatch, getState } }) => {
      const options = parseSearchQuery(location.search);
      if (!options.SearchableText && !options.Subject && !options.path) {
        return Promise.resolve();
      }

      const languageRoot = getSearchLanguageRoot(
        location.pathname,
        getState().intl?.locale,
      );

      return dispatch(
        searchContent(languageRoot, {
          ...options,
          use_site_search_settings: 1,
        }),
      );
    },
  },
])(Search);
